import { DEFAULT_LOCALE, LOCALES, type Locale } from '@nexus/core';
import i18next, { type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { ModuleMessages, WebModule } from '../modules/define';
import { de } from './de';
import { en } from './en';

export { useTranslation } from 'react-i18next';

type Tree = { [key: string]: string | Tree };

function nest(path: readonly string[], value: ModuleMessages): Tree {
  return path.reduceRight<Tree>((inner, segment) => ({ [segment]: inner }), value);
}

function merge(target: Tree, source: Tree): Tree {
  for (const [key, value] of Object.entries(source)) {
    const current = target[key];
    target[key] =
      typeof value === 'object' && typeof current === 'object'
        ? merge({ ...current }, value)
        : value;
  }
  return target;
}

/** Kernel texts plus the texts of every module below its ID. */
export function buildMessages(modules: readonly WebModule[]): Record<Locale, Tree> {
  const result = { de: structuredClone(de) as Tree, en: structuredClone(en) as Tree };
  for (const module of modules) {
    const path = module.manifest.id.split('.');
    for (const locale of LOCALES) merge(result[locale], nest(path, module.messages[locale]));
  }
  return result;
}

/** Locale of the browser, used until the personal setting is known (login page). */
export function browserLocale(): Locale {
  const preferred = navigator.languages.map((tag) => tag.slice(0, 2).toLowerCase());
  return (
    (preferred.find((tag) => (LOCALES as readonly string[]).includes(tag)) as Locale) ??
    DEFAULT_LOCALE
  );
}

export function createI18n(modules: readonly WebModule[], locale: Locale): i18n {
  const instance = i18next.createInstance();
  const messages = buildMessages(modules);
  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: LOCALES,
    resources: { de: { translation: messages.de }, en: { translation: messages.en } },
    // React escapes values already.
    interpolation: { escapeValue: false },
    initAsync: false,
  });
  return instance;
}
